import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor"]

  connect() {
    if (this.hasEditorTarget) {
      this.initializeEditor()
    }
  }

  initializeEditor() {
    // Ensure styles are applied by adding event listeners
    this.editorTargets.forEach(editor => {
      // Prevent file attachments
      editor.addEventListener("trix-file-accept", (e) => {
        e.preventDefault()
      })

      // Customize toolbar on initialization
      editor.addEventListener("trix-initialize", (e) => {
        this.simplifyToolbar(editor)
      })

      // Update style of text when formatting is applied
      editor.addEventListener("trix-change", () => {
        this.refreshEditor(editor)
      })

      // Also listen for focus to ensure styles are maintained
      editor.addEventListener("trix-focus", () => {
        editor.classList.add("trix-active")
      })

      editor.addEventListener("trix-blur", () => {
        editor.classList.remove("trix-active")
      })

      // Immediately simplify toolbar if it already exists
      const toolbar = editor.previousElementSibling
      if (toolbar && toolbar.tagName.toLowerCase() === 'trix-toolbar') {
        this.simplifyToolbar(editor)
      }

      // Initialize editor with proper styles
      this.refreshEditor(editor)
    })
  }

  simplifyToolbar(editor) {
    const toolbar = editor.previousElementSibling
    if (!toolbar || toolbar.tagName.toLowerCase() !== 'trix-toolbar') return

    // Allow only these button types - same for desktop and mobile
    const allowedButtons = [
      'bold', 'italic', 'strike', 
      'heading1', 'quote', 
      'bullet', 'number'
    ]
    
    // List of buttons to explicitly hide (comprehensive list)
    const buttonsToHide = [
      'attach',           // File attachments
      'link',             // Links
      'code',             // Code blocks
      'undo',             // Undo
      'redo',             // Redo
      'decrease-nesting-level',  // Decrease indent
      'increase-nesting-level'   // Increase indent
    ]
    
    // Find all buttons in the toolbar and hide unwanted ones
    Array.from(toolbar.querySelectorAll('.trix-button')).forEach(button => {
      // Extract the button type from the class name
      const buttonType = button.className.match(/trix-button--icon-([a-z0-9-]+)/)
      
      if (buttonType) {
        const type = buttonType[1]
        // Hide if it's explicitly in the hide list OR not in the allowed list
        if (buttonsToHide.includes(type) || !allowedButtons.includes(type)) {
          button.style.display = 'none'
        }
      } else {
        // Hide any button without a recognizable icon type (like text buttons)
        const isTextButton = !button.className.includes('trix-button--icon')
        if (isTextButton && !allowedButtons.some(allowed => button.textContent.toLowerCase().includes(allowed))) {
          button.style.display = 'none'
        }
      }
    })
    
    // Also hide any dialog-related buttons
    Array.from(toolbar.querySelectorAll('.trix-button--dialog')).forEach(button => {
      button.style.display = 'none'
    })
    
    // Hide dialog containers
    Array.from(toolbar.querySelectorAll('.trix-dialogs')).forEach(dialog => {
      dialog.style.display = 'none'
    })
    
    // Remove empty button groups
    Array.from(toolbar.querySelectorAll('.trix-button-group')).forEach(group => {
      const visibleButtons = Array.from(group.querySelectorAll('.trix-button')).filter(
        button => button.style.display !== 'none'
      )
      
      if (visibleButtons.length === 0) {
        group.style.display = 'none'
      }
    })
  }

  refreshEditor(editor) {
    // This small timeout helps ensure style changes are applied
    setTimeout(() => {
      // Force a layout recalculation to apply styles
      editor.style.display = 'none'
      editor.offsetHeight // Force a reflow
      editor.style.display = ''
    }, 0)
  }
} 
